<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <div class="info-content-container flex-column">
        <span class="sac__font-14 med">Step 2 of 3</span>
        <span class="sac__font-18 bold"> Job Types Mapping </span>
        <p class="sac__font-14 mt-2">
          Map your trade service offerings from SmartAC.com to your job types in ServiceTitan.
        </p>
      </div>
      <button type="button" class="ac-close-button" @click="toggle(true, 'titan-edit-integration')">
        <img src="@shared/assets/img/general/x-black.png" alt="x" class="ac-close" />
      </button>
    </div>
    <div class="ac-body-container">
      <div class="map-item-container">
        <div class="sac__font-14 semi-bold">ServiceTitan Business Unit</div>
        <div class="sac__font-14 semi-bold">ServiceTitan Job Type</div>
        <div style="width: 30px" />
        <div class="sac__font-14 semi-bold">Trade Category</div>
        <div class="sac__font-14 semi-bold">Trade Condition</div>
      </div>
      <div v-for="(item, idx) in serviceConditions" :key="idx" class="map-item-container">
        <SACDropdown
          :field="`${item.id}`"
          :change="handleChangeUnit"
          :value="getBusinessUnitId(item)"
          :options="unitOptions"
        />
        <SACDropdown
          :field="`${item.id}`"
          :change="handleChange"
          :value="getValue(item)"
          :options="jobTypesOptions(item)"
        />
        <img src="@shared/assets/img/general/mapping-icon.png" alt="arrows" class="img-size-24" />
        <span class="sac__font-16 n50">
          {{ item.category }}
        </span>
        <span class="sac__font-16 n50">
          {{ item.condition }}
        </span>
      </div>
    </div>
    <div class="ac-footer-container">
      <button
        type="button"
        class="info-form-button-outline mr-2"
        @click="toggle(true, 'titan-edit-integration')"
      >
        Cancel
      </button>
      <button type="button" class="info-form-button" @click="submit">Save</button>
    </div>
    <ProcessOverlay :show="updating || loadingMappings" :text="loadingText" />
  </div>
</template>

<script>
import customerDetailMixin from '@shared/mixins/customerDetailMixin';
import SACDropdown from '@shared/components/sac/SACDropdown.vue';
import { mapState } from 'vuex';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';

export default {
  name: 'MapJobTypesTitanPopup',
  components: {
    ProcessOverlay,
    SACDropdown,
  },
  mixins: [customerDetailMixin],
  props: {
    toggle: Function,
  },
  data() {
    return {
      mappings: [],
      updating: false,
      loadingText: 'Saving..',
    };
  },
  computed: {
    ...mapState({
      jobTypes: (state) => state.serviceTitan.jobTypes,
      serviceConditions: (state) => state.serviceTitan.serviceConditions,
      jobTypeMappings: (state) => state.serviceTitan.jobTypeMappings,
      loadingMappings: (state) => state.serviceTitan.loadingMappings,
    }),
    unitOptions() {
      if (Object.keys(this.jobTypes).length === 0) {
        return [];
      }
      return [
        ...Object.values(this.jobTypes.businessUnits).map((p) => ({
          label: p.name,
          value: p.id,
        })),
      ];
    },
  },
  watch: {
    jobTypeMappings: {
      handler(newVal, oldVal) {
        this.mappings =
          Array.isArray(newVal) && newVal.length
            ? newVal.map((el) => ({
                ...el,
                externalId: +el.externalId,
                externalBusinessUnitId: this.setBusinessUnitId(el),
                internalId: +el.internalId,
              }))
            : [];
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.initialLoad();
  },
  methods: {
    initialLoad() {
      this.loadingText = 'Loading...';
      if (this.serviceConditions?.length === 1 || (this.jobTypes && this.jobTypes.length === 0)) {
        this.$store.dispatch('getServiceConditionList');
        this.$store.dispatch('getJobTypes');
      }
      if (this.jobTypeMappings?.length === 0) {
        this.$store.dispatch('queryMappingsByCorpId');
      }
    },
    setBusinessUnitId(item) {
      return item.externalBusinessUnitId != null
        ? Number(item.externalBusinessUnitId)
        : Object.values(this.jobTypes.jobTypes).find((x) => x.id === +item.externalId)
            ?.businessUnitIds[0];
    },
    handleChangeUnit(field, value) {
      const mapping = this.mappings.find((x) => x.internalId === +field);
      if (mapping) {
        mapping.externalBusinessUnitId = value;
      } else {
        this.mappings.push({
          internalId: +field,
          externalBusinessUnitId: value,
          mapType: 'JobType',
          externalSource: 'Service Titan',
          updateBy: localStorage.loginEmail,
        });
      }
    },
    handleChange(field, value) {
      const mapping = this.mappings.find((x) => x.internalId === +field);
      const index = this.mappings.indexOf(mapping);
      if (mapping) {
        this.mappings.splice(index, 1);
        this.mappings.push({
          ...mapping,
          externalId: value,
        });
      }
    },
    getValue(item) {
      const val = this.mappings.find((x) => x.internalId === item.id);
      return val?.externalId;
    },
    getBusinessUnitId(item) {
      const val = this.mappings.find((x) => x.internalId === item.id);
      return val?.externalBusinessUnitId;
    },
    jobTypesOptions(item) {
      const unitId = this.mappings.find((x) => x.internalId === item.id)?.externalBusinessUnitId;
      if (unitId) {
        const { jobTypes } = this.jobTypes;
        const items = Object.values(jobTypes).filter((x) => x.businessUnitIds.includes(unitId));
        return items.map((p) => ({
          label: p.name,
          value: p.id,
        }));
      }
      return [];
    },
    async submit() {
      try {
        this.loadingText = 'Saving..';
        this.updating = true;
        await this.$store.dispatch('setJobTypeMappings', this.mappings);
        this.toggle(true, 'titan-edit-integration');
      } finally {
        this.updating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ac-card-content {
  .ac-header-container {
    display: flex;
    justify-content: space-between;
    .info-content-container {
      align-items: flex-start;
      display: inline-flex;
      gap: 8px;
      width: 100%;
    }
  }
  .map-item-container {
    display: grid;
    grid-template-columns: 1fr 1fr auto 0.7fr 0.8fr;
    gap: 1rem;
    align-items: center;
    min-height: 3rem;
    justify-content: center;
  }
  .loading-img {
    margin-top: 45px;
    margin-bottom: 45px;
    height: 45px;
  }
  .info-form-button {
    padding: 12px;
    height: 48px;
    border-radius: 6px;
    background-color: $NBLUE100;
    font-weight: 600;
    color: $WHITE;
    width: 155px;
  }
  .info-form-button-outline {
    padding: 12px;
    height: 48px;
    border-radius: 4px;
    border: 2px solid $NBLUE100;
    color: $NBLUE100;
    font-weight: 600;
    width: 155px;
  }
}
</style>
